// Marketplace funnel events for GA4 (gtag.js).
//
// Consent is handled once, by Google Consent Mode, and deliberately not again
// here: public/gtag-init.js declares the default from the choice the visitor
// already stored, and CookieConsentContext.js updates it whenever that choice
// changes. A visitor who has not accepted therefore contributes cookieless,
// unidentified pings; one who accepted contributes attributed events. Gating
// these calls a second time on our side would drop the modelled traffic without
// collecting anything less.
//
// Nothing sent from this file may carry personal data - no names, emails, phone
// numbers, addresses, payment receipts or auth tokens. Google's terms forbid it
// and a property that receives it can be purged. Everything below is limited to
// product, taxonomy and amount fields.

const asAmount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.round(parsed * 100) / 100;
};

const withoutEmpty = (params) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
);

export const trackEvent = (name, params = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', name, withoutEmpty(params));
  } catch (error) {
    // Measurement must never break the flow the visitor is in.
  }
};

// GA4 only reads `value` when it is paired with a currency, so the two are
// always emitted together or not at all.
export const moneyParams = (currency, value) => {
  const amount = asAmount(value);
  if (!currency || amount === undefined) return {};
  return { currency: String(currency).toUpperCase(), value: amount };
};

// One `items` entry. Product payloads reach the app under three different
// shapes - the product detail endpoint, the cart rows and the order rows - so
// each field accepts every spelling in use rather than forcing callers to
// normalise first.
export const analyticsItem = (source = {}, quantity) => {
  const id = source.productid ?? source.idproducts ?? source.product_id ?? source.id;
  return withoutEmpty({
    item_id: id === undefined || id === null ? undefined : String(id),
    item_name: source.product_brand || source.product_name || source.product_sku,
    item_brand: source.product_manufacturer_brand || source.manufacturer_brand,
    item_category: source.product_sector || source.sector,
    item_category2: source.product_category || source.category,
    item_category3: source.product_subcategory || source.subcategory,
    price: asAmount(source.product_price ?? source.price ?? source.unit_price),
    quantity: asAmount(quantity ?? source.quantity),
  });
};

export const analyticsItems = (rows = []) => (
  Array.isArray(rows) ? rows.slice(0, 200).map((row) => analyticsItem(row)) : []
);
